/**
 * Single source of truth for every user-facing string in the bulk-alerts UI
 * (KIR-3252) — the selection-bar summaries, the cross-page affordances, the
 * state-aware action-button labels, and the confirmation-modal copy.
 *
 * Centralized + pure (no React) so the exact singular/plural, filter-aware, and
 * mixed-state wording from the ticket is exhaustively unit-testable. The action
 * labels are derived from the backend eligibility counts
 * (`BulkAlertEligibilityResponse`).
 *
 * `override-severity` and `reset-severity` (KIR-5192) have their own copy at
 * the bottom: they are not suppression toggles, so they have no duration.
 * Override has no eligible/already split in the preview; reset does
 * (`eligibleForResetSeverity` / `withoutSeverityOverride`), read through
 * {@link modalCountsForAction} like the suppression actions.
 */
import type {
  BulkAlertAction,
  BulkAlertEligibilityResponse,
} from '@kirin/api-client/contracts/alerts';
import type { Severity } from '@kirin/api-client/contracts/shared/severity';
import { getSeverityLabel } from '@kirin/api-client/helpers/severity';
import { SEVERITY_OVERRIDE_REACH } from '@/components/alertSeverityCopy';

/** The four suppression actions (mute / exclude and their inverses). */
export type BulkSuppressionAction = Exclude<
  BulkAlertAction,
  'override-severity' | 'reset-severity'
>;

// ---------------------------------------------------------------------------
// Grammar helpers
// ---------------------------------------------------------------------------

/** Lowercase noun for prose ("1 alert" / "5 alerts"). */
export function alertsNoun(count: number): string {
  return count === 1 ? 'alert' : 'alerts';
}

/** Title-case noun for button labels ("Mute Alert" / "Mute Alerts"). */
export function alertsLabel(count: number): string {
  return count === 1 ? 'Alert' : 'Alerts';
}

function filterNoun(filterCount: number): string {
  return filterCount === 1 ? 'filter' : 'filters';
}

function thisThese(filterCount: number): string {
  return filterCount === 1 ? 'this' : 'these';
}

function isAre(count: number): string {
  return count === 1 ? 'is' : 'are';
}

// ---------------------------------------------------------------------------
// Selection-bar summaries
// ---------------------------------------------------------------------------

/**
 * Selection summary: "X alert/s selected." — or, when any filter is applied,
 * "X alert/s matching filter/s selected." Used for a page selection and for a
 * select-all with some alerts excluded (count = total − excluded); the bar
 * names no pages either way. `filterCount` is the number of active column
 * filters (0 ⇒ no filter wording). Pass ≥1 when only a search term is active
 * so the filter-aware variant is used.
 */
export function selectionSummary(args: {
  count: number;
  filterCount: number;
}): string {
  const { count, filterCount } = args;
  const noun = alertsNoun(count);
  if (filterCount > 0) {
    return `${count} ${noun} matching ${filterNoun(filterCount)} selected.`;
  }
  return `${count} ${noun} selected.`;
}

/**
 * The "select all across pages" affordance shown when the whole current page is
 * selected and more pages exist: "Select all X alerts".
 */
export function selectAllAcrossPagesLabel(args: {
  total: number;
  filterCount: number;
}): string {
  const { total, filterCount } = args;
  const noun = alertsNoun(total);
  if (filterCount > 0) {
    return `Select all ${total} ${noun} matching ${thisThese(filterCount)} ${filterNoun(filterCount)}`;
  }
  return `Select all ${total} ${noun}`;
}

/**
 * Summary shown once the user has chosen "select all across pages".
 * "Selected all X alerts." (filter-aware variant included).
 */
export function selectedAllMatchingSummary(args: {
  total: number;
  filterCount: number;
}): string {
  const { total, filterCount } = args;
  const noun = alertsNoun(total);
  if (filterCount > 0) {
    return `Selected all ${total} ${noun} matching ${thisThese(filterCount)} ${filterNoun(filterCount)}.`;
  }
  return `Selected all ${total} ${noun}.`;
}

// ---------------------------------------------------------------------------
// State-aware action labels (from eligibility counts)
// ---------------------------------------------------------------------------

export interface BulkBarAction {
  action: BulkSuppressionAction;
  label: string;
  /** Alerts eligible for this action (the count the modal will confirm). */
  eligibleCount: number;
}

/**
 * Mute/unmute buttons to show. All-unmuted → only "Mute Alert/s" (no count);
 * all-muted → only "Unmute Alert/s"; mixed → both, each with its count. An
 * action with zero eligible alerts is omitted.
 */
export function muteUnmuteActions(
  elig: BulkAlertEligibilityResponse
): BulkBarAction[] {
  const mute = elig.eligibleForMute;
  const unmute = elig.eligibleForUnmute;
  const mixed = mute > 0 && unmute > 0;
  const actions: BulkBarAction[] = [];
  if (mute > 0) {
    actions.push({
      action: 'mute',
      eligibleCount: mute,
      // Mixed → count + singular/plural ("Mute 3 Alerts"); single-state → no
      // count, singular/plural noun ("Mute Alert" / "Mute Alerts") per ticket.
      label: mixed
        ? `Mute ${mute} ${alertsLabel(mute)}`
        : `Mute ${alertsLabel(mute)}`,
    });
  }
  if (unmute > 0) {
    actions.push({
      action: 'unmute',
      eligibleCount: unmute,
      label: mixed
        ? `Unmute ${unmute} ${alertsLabel(unmute)}`
        : `Unmute ${alertsLabel(unmute)}`,
    });
  }
  return actions;
}

export interface ExclusionActionsResult {
  actions: BulkBarAction[];
  /** "Exclusion is not available for X selected alert/s" when any are ineligible. */
  ineligibleNote?: string;
}

/**
 * Create/remove-exclusion buttons + the ineligibility note. None-excluded →
 * only "Create Exclusion for Alert/s"; all-excluded → only "Remove Exclusion
 * for Alert/s"; mixed → both with counts. Zero-eligible actions omitted.
 */
export function exclusionActions(
  elig: BulkAlertEligibilityResponse
): ExclusionActionsResult {
  const create = elig.eligibleForExclusion;
  const remove = elig.eligibleForRemoveExclusion;
  const mixed = create > 0 && remove > 0;
  const actions: BulkBarAction[] = [];
  if (create > 0) {
    actions.push({
      action: 'exclude',
      eligibleCount: create,
      label: mixed
        ? `Create Exclusion for ${create} ${alertsLabel(create)}`
        : `Create Exclusion for ${alertsLabel(create)}`,
    });
  }
  if (remove > 0) {
    actions.push({
      action: 'remove-exclusion',
      eligibleCount: remove,
      label: mixed
        ? `Remove Exclusion from ${remove} ${alertsLabel(remove)}`
        : `Remove Exclusion for ${alertsLabel(remove)}`,
    });
  }
  const ineligibleNote =
    elig.exclusionIneligible > 0
      ? `Exclusion is not available for ${elig.exclusionIneligible} selected ${alertsNoun(elig.exclusionIneligible)}`
      : undefined;
  return { actions, ineligibleNote };
}

// ---------------------------------------------------------------------------
// Confirmation-modal copy
// ---------------------------------------------------------------------------

export interface BulkModalCopy {
  title: string;
  /** Bold sub-heading (exclusion warning header) — present only for exclude. */
  subtitle?: string;
  /** Impact paragraph (mute silence note / exclusion warning body). */
  body?: string;
  /** "[eligible] selected alert/s will be …" line. */
  countLine: string;
  /** "[already] selected alert/s are/is already … and will not change." Omitted when 0. */
  alreadyLine?: string;
  confirmLabel: string;
  /** Destructive (red) confirm button — exclusion only. */
  destructive: boolean;
  /** Whether the modal shows the duration picker (mute / create-exclusion). */
  needsDuration: boolean;
}

/**
 * Copy for the per-action confirmation modal, matching the ticket's modal
 * table. `eligibleCount` drives singular/plural; `alreadyCount` (already in the
 * target state) renders the "will not change" line, omitted when zero.
 */
export function bulkModalCopy(
  action: BulkSuppressionAction,
  counts: { eligibleCount: number; alreadyCount: number }
): BulkModalCopy {
  const e = counts.eligibleCount;
  const a = counts.alreadyCount;
  const single = e === 1;

  switch (action) {
    case 'mute':
      return {
        title: single ? 'Mute Alert?' : `Mute ${e} Alerts?`,
        body: single
          ? 'Silence this alert for your workspace. Detection and enforcement will continue.'
          : 'Silence these alerts for your workspace. Detection and enforcement will continue.',
        countLine: single
          ? '1 selected alert will be muted.'
          : `${e} selected alerts will be muted.`,
        alreadyLine:
          a > 0
            ? `${a} selected ${alertsNoun(a)} ${isAre(a)} already muted and will not change.`
            : undefined,
        confirmLabel: single ? 'Mute Alert' : 'Mute Alerts',
        destructive: false,
        needsDuration: true,
      };
    case 'unmute':
      return {
        title: single ? 'Unmute Alert?' : `Unmute ${e} Alerts?`,
        countLine: single
          ? '1 selected alert will be unmuted.'
          : `${e} selected alerts will be unmuted.`,
        alreadyLine:
          a > 0
            ? `${a} selected ${alertsNoun(a)} ${isAre(a)} already unmuted and will not change.`
            : undefined,
        confirmLabel: single ? 'Unmute Alert' : 'Unmute Alerts',
        destructive: false,
        needsDuration: false,
      };
    case 'exclude':
      return {
        title: single
          ? 'Create Exclusion - Warning'
          : `Create ${e} Exclusions - Warning`,
        subtitle: single
          ? 'Creating an exclusion will disable prevention for activity matching this alert.'
          : 'Creating an exclusion will disable prevention for activity matching these alerts.',
        body: single
          ? 'Activity matching this pattern will no longer trigger enforcement actions and threats may proceed unblocked and undetected. Create exclusions only for alerts you have fully investigated and confirmed as safe.'
          : 'Activity matching these patterns will no longer trigger enforcement actions and threats may proceed unblocked and undetected. Create exclusions only for alerts you have fully investigated and confirmed as safe.',
        countLine: single
          ? '1 selected alert will be excluded.'
          : `${e} selected alerts will be excluded.`,
        alreadyLine:
          a > 0
            ? `${a} selected ${alertsNoun(a)} ${isAre(a)} already excluded and will not change.`
            : undefined,
        confirmLabel: single ? 'Create Exclusion' : 'Create Exclusions',
        destructive: true,
        needsDuration: true,
      };
    case 'remove-exclusion':
      return {
        title: single
          ? 'Remove Exclusion from Alert?'
          : `Remove Exclusions from ${e} Alerts?`,
        countLine: single
          ? '1 selected alert exclusion will be removed.'
          : `${e} selected alerts exclusions will be removed.`,
        alreadyLine:
          a > 0
            ? a === 1
              ? "1 selected alert already doesn't have exclusion and will not change."
              : `${a} selected alerts already don't have exclusions and will not change.`
            : undefined,
        confirmLabel: single ? 'Remove Exclusion' : 'Remove Exclusions',
        destructive: false,
        needsDuration: false,
      };
  }
}

// ---------------------------------------------------------------------------
// Result toasts (all-or-nothing → single success/failure line, no undo)
// ---------------------------------------------------------------------------

/** Success toast: "X Alert/s muted" / "X Exclusion/s removed", etc. */
export function bulkActionSuccessToast(
  action: BulkSuppressionAction,
  changedCount: number
): string {
  const n = changedCount;
  switch (action) {
    case 'mute':
      return `${n} ${alertsLabel(n)} muted`;
    case 'unmute':
      return `${n} ${alertsLabel(n)} unmuted`;
    case 'exclude':
      return `${n} ${alertsLabel(n)} excluded`;
    case 'remove-exclusion':
      return `${n} ${n === 1 ? 'Exclusion' : 'Exclusions'} removed`;
  }
}

/** Failure toast: generic per-action message (no per-row reason — all-or-nothing). */
export function bulkActionFailureToast(action: BulkSuppressionAction): string {
  switch (action) {
    case 'mute':
      return 'Mute action failed';
    case 'unmute':
      return 'Unmute action failed';
    case 'exclude':
      return 'Exclude action failed';
    case 'remove-exclusion':
      return 'Remove exclusion action failed';
  }
}

/**
 * The eligible / already-in-target-state counts for an action, sourced from the
 * eligibility preview — used to parameterize the confirmation modal. For
 * `reset-severity` "already" means "has no override". Its two buckets are
 * absent while `alert_severity_override` is off, which reads as 0.
 */
export function modalCountsForAction(
  action: BulkSuppressionAction | 'reset-severity',
  elig: BulkAlertEligibilityResponse
): { eligibleCount: number; alreadyCount: number } {
  switch (action) {
    case 'reset-severity':
      return {
        eligibleCount: elig.eligibleForResetSeverity ?? 0,
        alreadyCount: elig.withoutSeverityOverride ?? 0,
      };
    case 'mute':
      return {
        eligibleCount: elig.eligibleForMute,
        alreadyCount: elig.alreadyMuted,
      };
    case 'unmute':
      return {
        eligibleCount: elig.eligibleForUnmute,
        alreadyCount: elig.alreadyUnmuted,
      };
    case 'exclude':
      return {
        eligibleCount: elig.eligibleForExclusion,
        alreadyCount: elig.alreadyExcluded,
      };
    case 'remove-exclusion':
      return {
        eligibleCount: elig.eligibleForRemoveExclusion,
        alreadyCount: elig.withoutExclusion,
      };
  }
}

// ---------------------------------------------------------------------------
// Override severity (KIR-5192)
// ---------------------------------------------------------------------------

/** Bulk-bar trigger label for the severity menu. */
export const OVERRIDE_SEVERITY_LABEL = 'Override Severity';

export interface BulkSeverityModalCopy {
  title: string;
  body: string;
  /** Omitted when there is nothing to say (reset, every alert overridden). */
  countLine?: string;
  confirmLabel: string;
}

/**
 * Confirmation-modal copy for a bulk severity override. `totalSelected` is the
 * server-resolved count for the live selection (eligibility preview), never a
 * client-side count.
 */
export function bulkSeverityModalCopy(
  severity: Severity,
  totalSelected: number
): BulkSeverityModalCopy {
  const n = totalSelected;
  const level = getSeverityLabel(severity);
  return {
    title:
      n === 1
        ? `Override Severity to ${level}?`
        : `Override Severity of ${n} Alerts to ${level}?`,
    body:
      n === 1
        ? `This will update the severity assigned to this alert. ${SEVERITY_OVERRIDE_REACH}`
        : `This will update the severity assigned to these alerts. ${SEVERITY_OVERRIDE_REACH}`,
    countLine:
      n === 1
        ? `1 selected alert will be set to ${level}.`
        : `${n} selected alerts will be set to ${level}.`,
    confirmLabel: OVERRIDE_SEVERITY_LABEL,
  };
}

/**
 * Success toast: "N alerts set to High", plus "M were already overridden to
 * High" when the backend reports any. The "already" count comes from the
 * server; it is not selected − changed, which would also count alerts the
 * server skipped for other reasons.
 */
export function bulkSeveritySuccessToast(
  severity: Severity,
  counts: { changedCount: number; alreadyOverriddenCount: number }
): { title: string; description?: string } {
  const level = getSeverityLabel(severity);
  const n = counts.changedCount;
  const m = counts.alreadyOverriddenCount;
  return {
    title: `${n} ${alertsNoun(n)} set to ${level}`,
    ...(m > 0
      ? {
          description: `${m} ${m === 1 ? 'was' : 'were'} already overridden to ${level}`,
        }
      : {}),
  };
}

export const BULK_SEVERITY_FAILURE_TOAST = 'Override severity action failed';

/**
 * Menu item for bulk reset. Fixed, with no count even for a mixed selection
 * (unlike "Unmute 3 Alerts"): it mirrors the single-alert "Reset to Default:
 * High" without the level, since each alert returns to its own. The dialog
 * carries the counts.
 */
export const RESET_SEVERITY_LABEL = 'Reset to Default';

/**
 * Confirmation-modal copy for a bulk severity reset. `eligibleCount` is the
 * selected alerts with an override (the ones that will change) and
 * `alreadyCount` the ones without, both from the eligibility preview — the
 * same split the unmute dialog uses.
 */
export function bulkSeverityResetModalCopy(counts: {
  eligibleCount: number;
  alreadyCount: number;
}): BulkSeverityModalCopy {
  const e = counts.eligibleCount;
  const m = counts.alreadyCount;
  return {
    title:
      e === 1
        ? 'Reset Severity to Default?'
        : `Reset Severity of ${e} Alerts to Default?`,
    body:
      e === 1
        ? "This alert will return to the severity Kirin assigned, and future matching alerts will follow Kirin's assessment again."
        : "These alerts will return to the severity Kirin assigned, and future matching alerts will follow Kirin's assessment again.",
    ...(m > 0
      ? {
          countLine:
            m === 1
              ? '1 selected alert has no override and will not change.'
              : `${m} selected alerts have no override and will not change.`,
        }
      : {}),
    confirmLabel: RESET_SEVERITY_LABEL,
  };
}

/**
 * Success toast for a bulk reset: "N alert/s reset to default", plus "M had no
 * override" when the backend reports any. With nothing reset (someone cleared
 * the overrides between the preview and the confirm) it says so instead.
 */
export function bulkSeverityResetSuccessToast(counts: {
  changedCount: number;
  notOverriddenCount: number;
}): { title: string; description?: string } {
  const n = counts.changedCount;
  const m = counts.notOverriddenCount;
  if (n === 0) {
    return { title: 'No alerts had an override' };
  }
  return {
    title: `${n} ${alertsNoun(n)} reset to default`,
    ...(m > 0 ? { description: `${m} had no override` } : {}),
  };
}

export const BULK_SEVERITY_RESET_FAILURE_TOAST = 'Reset severity action failed';
